<template>
  <div class="home-view" data-cy="home-view">
    <div
      v-if="!main.companySummary"
      class="home-view__notification"
      data-cy="home-view-notification"
    >
      Select a company!!
    </div>
    <div class="home-view__container">
      <div v-if="companySelected" class="home-view__column">
        <CompanyModule />
        <DownloadsModule />
        <SummaryModule />
        <LinksModule />
        <HelpModule />
      </div>
      <div class="home-view__column">
        <NewsModule />
        <SearchModule />
        <CartsModule
          v-if="showCartsModule"
          :canModifyIngredientCarts="canOpenOrModifyIngredientCarts"
          :canModifyProductCarts="canOpenOrModifyProductCarts"
        />
        <InfoModule v-if="companySelected" />
        <BugCreationModule v-if="showBugCreationModule" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CompanyModule from "./CompanyModule.vue";
import DownloadsModule from "./DownloadsModule.vue";
import SummaryModule from "./SummaryModule.vue";
import LinksModule from "./LinksModule.vue";
import HelpModule from "./HelpModule.vue";
import NewsModule from "./NewsModule.vue";
import SearchModule from "./SearchModule.vue";
import { useMainStore } from "@/stores/useMainStore";
import BugCreationModule from "./BugCreationModule.vue";
import CartsModule from "./CartsModule.vue";
import InfoModule from "./InfoModule.vue";
import { computed, onBeforeMount, ref } from "vue";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";
import {
  IngredientsAppGroup,
  ProductAppGroup,
} from "@/shared/access/RoleGroups";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import { UserRoles } from "@/Domain/UserRoles";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { CacheService } from "@/shared/services/CacheService";

const main = useMainStore();

const gpcPlantId = ref<string>("");
const gpcPlantIdLoaded = ref(false);

// Don't show the first column, carts and info modules if no company is selected
//  (ie. userRoles who are emulators or error getting company)
const companySelected = computed(() => {
  return main.companySummary !== null && main.companySummary !== undefined;
});

const showBugCreationModule = computed(() => {
  return main.isInAnyRole([
    UserRoles.ROLE_MIS,
    UserRoles.ROLE_IN_HOUSE_MIS_MANAGER,
  ]);
});

const showCartsModule = computed(() => {
  return (
    companySelected.value &&
    (canOpenOrModifyProductCarts.value || canOpenOrModifyIngredientCarts.value)
  );
});

const canOpenOrModifyProductCarts = computed(() => {
  return (
    (main.companySummary?.denials?.every(
      (d) => !d.Products || d.Products.toUpperCase() !== "ALL"
    ) &&
      main.companySummary.status !== EnumCompanyStatus.Pending &&
      gpcPlantIdLoaded.value &&
      !gpcPlantId.value &&
      ProductAppGroup.roles.some((r) => main.isInRole(r))) ??
    false
  );
});

const canOpenOrModifyIngredientCarts = computed(() => {
  return (
    (main.companySummary?.denials?.every(
      (d) => !d.IA || d.IA.toUpperCase() !== "ALL"
    ) &&
      IngredientsAppGroup.roles.some((r) => main.isInRole(r))) ??
    false
  );
});

const updateGpcPlantIdAsync = async function () {
  const gpcPlantIdParams = {
    userLogin: main.userLogin,
  };
  return handleResponsePromiseAsync(
    CacheService.getResponseAsync(
      createCacheKey(gpcPlantIdParams, "GetGpcPlantId"),
      () => api.get("GetGpcPlantId", gpcPlantIdParams)
    ),
    (data) => {
      gpcPlantId.value = data;
      gpcPlantIdLoaded.value = true;
    },
    "ScheduleBView",
    "updateGpcPlantId"
  );
};

onBeforeMount(() => {
  updateGpcPlantIdAsync();
});
</script>

<style lang="scss">
.home-view {
  .tab:not(.modal-container .tab) {
    border-bottom: none;
    border-top: 2px solid transparent;
    &.active {
      color: inherit;
      font-weight: 400;
      background: white;
      border-color: #425ca1;
    }
  }
}
</style>

<style scoped lang="scss">
.home-view {
  &__notification {
    margin: 30px 30px 20px;
    color: #3c763d;
    background-color: #dff0d8;
    border-color: #d6e9c6;
    text-align: center;
    padding: 1rem 1rem;
  }

  &__container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: wrap;
    row-gap: 60px;
    max-width: 1440px;
    margin: auto;
    padding-bottom: 40px;
  }

  &__column {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0 20px;

    @include media-breakpoint-up(md) {
      width: 50%;
    }
  }
}
</style>
